import React, { useEffect, useState } from 'react';
import { Field, Form, Formik } from 'formik';
import { useApi } from '../../context/ApiProvider';
import ActivityIndicator from '../Helpers/ActivityIndicator';
import Card from '../Helpers/Card';
import './McpSettings.css';

const defaultMcpSettings = {
  enabled: false,
  writes: {
    activities: false,
    goals: false,
    keyResults: false,
  },
};

function normalizeMcpSettings(mcp) {
  return {
    ...defaultMcpSettings,
    ...(mcp || {}),
    enabled: mcp?.enabled === true,
    writes: {
      ...defaultMcpSettings.writes,
      ...(mcp?.writes || {}),
    },
  };
}

export default function McpSettings({ clientId }) {
  const Api = useApi();
  const [settings, setSettings] = useState(defaultMcpSettings);
  const [isLoading, setIsLoading] = useState(true);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState(false);

  useEffect(() => {
    if (!clientId) return;

    setIsLoading(true);
    Api.get(`/rolf/clients/${clientId}/settings/mcp`)
      .then((data) => setSettings(normalizeMcpSettings(data?.mcp)))
      .catch((error) => setSaveError(error.error || 'Could not load Rolf AI settings'))
      .finally(() => setIsLoading(false));
  }, [Api, clientId]);

  const saveSettings = async (mcp) => {
    setSaveError(false);
    setSaveSuccess(false);

    try {
      await Api.post(`/rolf/clients/${clientId}/settings/`, { mcp });
      setSettings(normalizeMcpSettings(mcp));
      setSaveSuccess('Rolf AI settings saved');
    } catch (error) {
      setSaveError(error.error || 'Could not save Rolf AI settings');
    }
  };

  if (isLoading) return <ActivityIndicator color="black" />;

  return (
    <section>
      <h4>Rolf AI (MCP)</h4>
      <Card className="mcpSettingsCard">
        <Formik
          initialValues={settings}
          enableReinitialize
          onSubmit={async (values, { setSubmitting }) => {
            await saveSettings(values);
            setSubmitting(false);
          }}
        >
          {({ isSubmitting }) => (
            <Form>
              <p className="meta mcpSettingsIntro">Connects this customer’s Rolf accounts to approved AI clients through OAuth. Read-only access is enabled first; each write capability must be allowed separately.</p>
              <label className="checkbox mcpSettingsToggle">
                <Field type="checkbox" name="enabled" />
                <span>
                  <strong>Enable Rolf AI connection</strong>
                  <span className="meta">Users who belong to more than one customer can connect only when every customer has explicitly enabled Rolf AI.</span>
                </span>
              </label>
              <div className="mcpSettingsWrites">
                <h5>Allow proposed changes</h5>
                <p className="meta">Each capability must be explicitly enabled. Rolf AI will always ask for confirmation before it makes a change.</p>
                <div className="mcpSettingsWriteOptions">
                  <label className="checkbox mcpSettingsWriteOption">
                    <Field type="checkbox" name="writes.activities" />
                    <strong>Activities</strong>
                  </label>
                  <label className="checkbox mcpSettingsWriteOption">
                    <Field type="checkbox" name="writes.goals" />
                    <strong>Goals</strong>
                  </label>
                  <label className="checkbox mcpSettingsWriteOption">
                    <Field type="checkbox" name="writes.keyResults" />
                    <strong>Key results</strong>
                  </label>
                </div>
              </div>
              <div className="mcpSettingsActions">
                <button type="submit" disabled={isSubmitting}>Save Rolf AI settings {isSubmitting && <ActivityIndicator />}</button>
                {saveSuccess && <div className="formControlSuccess">{saveSuccess}</div>}
                {saveError && <div className="formControlError">{saveError}</div>}
              </div>
            </Form>
          )}
        </Formik>
      </Card>
    </section>
  );
}
